import { Alert, Button, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../../common/lib/useDebounce';
import { FormError } from '../../../types/FormError';
import { LoadState } from '../../../types/LoadState';
import * as varderingClient from '../../clients/VarderingClient';
import {
  BostadAutocompleteDto,
  BostadsinformationDto,
  BostadsvarderingDto,
} from '../../types/dtos/VarderingarModel';
import { OptionalTooltip } from '../common/OptionalTooltip';
import { AdressAutocomplete, mapSelectedAdress } from './AdressAutocomplete';
import { KommunerAutocomplete } from './KommunerAutocomplete';
import { LagenheterSelect } from './LagenheterSelect';
import { Fetcher } from '../common/Fetcher';

interface AdressAutocompleteProps {
  setActiveVardering: (bostadsvardering: BostadsvarderingDto) => void;
  setBostadsinformation: (bostadsinformation: BostadsinformationDto) => void;
}

export const VarderingAdressForm: React.FC<AdressAutocompleteProps> = ({
  setBostadsinformation,
  setActiveVardering,
}) => {
  const [selectedKommun, setSelectedKommun] = useState('');
  const [kommunValue, setKommunValue] = useState('');

  const [adressValue, setAdressValue] = useState('');
  const [adressOptions, setAdressOptions] = useState<BostadAutocompleteDto[]>(
    []
  );
  const [selectedAdress, setSelectedAdress] = useState<BostadAutocompleteDto>();
  const debouncedAdressValue = useDebounce(adressValue, 300);

  const [bostad, setBostad] = useState<BostadsinformationDto>();
  const [adressLoadState, setAdressLoadState] = useState(LoadState.Idle);
  const [error, setError] = useState<FormError>();
  const [lagenhetsnummers, setLagenhetsnummers] = useState<
    BostadAutocompleteDto[]
  >([]);
  const [selectedLagenhetsnummer, setSelectedLagenhetsnummer] =
    useState<BostadAutocompleteDto>();
  const [lagenhetLoadState, setLagenhetLoadState] = useState(LoadState.Idle);
  const queuedRequest = useRef<string>();

  const getBostadsInformationForId = async (id: number) => {
    const response = await varderingClient.getBostadFromBostadsregistret(id);

    if (response.success) {
      setBostad(response.data);
    }
  };

  useEffect(() => {
    if (
      selectedAdress &&
      mapSelectedAdress(selectedAdress) !== debouncedAdressValue
    ) {
      setSelectedAdress(undefined);
    }
    if (!debouncedAdressValue) {
      return setError(undefined);
    }
    if (!selectedKommun) {
      return setError({
        text: 'Ange en kommun.',
        severity: 'warning',
      });
    }

    const fetchAdressAutocomplete = async (
      kommun: string,
      searchString: string
    ) => {
      if (adressLoadState === LoadState.Loading) {
        queuedRequest.current = debouncedAdressValue;
        return;
      }
      setError(undefined);
      setAdressLoadState(LoadState.Loading);
      const response = await varderingClient.getAutocompleteAdress(
        kommun,
        searchString
      );
      if (!response.success) {
        setAdressLoadState(LoadState.Error);
        return setError({
          text: 'Kunde inte hämta adresser.',
          severity: 'error',
        });
      }
      setAdressLoadState(LoadState.Success);
      setAdressOptions(
        response.data?.length || !selectedAdress
          ? response.data
          : [selectedAdress]
      );
      if (queuedRequest.current) {
        fetchAdressAutocomplete(kommun, queuedRequest.current);
        queuedRequest.current = undefined;
      }
    };

    if (debouncedAdressValue) {
      fetchAdressAutocomplete(selectedKommun, debouncedAdressValue);
    }
  }, [debouncedAdressValue]);

  useEffect(() => {
    setLagenhetsnummers([]);
    const searchForAdressInBostadsregistret = async (
      bostadAutocomplete?: BostadAutocompleteDto
    ) => {
      setLagenhetLoadState(LoadState.Loading);
      const response = await varderingClient.getDataFromBostadsregistret(
        bostadAutocomplete.postort,
        bostadAutocomplete.text
      );

      if (!response.success) {
        return setLagenhetLoadState(LoadState.Error);
      }
      setLagenhetLoadState(LoadState.Success);
      const bostadAutocompletes = response.data;
      if (bostadAutocompletes.filter((x) => x.lagenhetsnummer).length > 1) {
        setLagenhetsnummers(bostadAutocompletes);
      } else if (bostadAutocompletes.length === 1) {
        getBostadsInformationForId(bostadAutocompletes[0].id);
      } else {
        getCoordinatesForAdress(
          bostadAutocomplete.postort,
          bostadAutocomplete.text
        );
      }
    };

    const getCoordinatesForAdress = async (postort: string, adress: string) => {
      const response = await varderingClient.getCoordinatesForAdress(
        postort,
        adress
      );

      if (response.success) {
        setBostad(response.data);
      }
    };

    const getBostadsInformationForId = async (id: number) => {
      const response = await varderingClient.getBostadFromBostadsregistret(id);

      if (response.success) {
        setBostad(response.data);
      }
    };

    if (selectedAdress) {
      if (selectedAdress.id !== 0) {
        getBostadsInformationForId(selectedAdress.id);
      } else if (selectedAdress.selectable) {
        searchForAdressInBostadsregistret(selectedAdress);
      }
    }
  }, [selectedAdress]);

  useEffect(() => {
    if (selectedLagenhetsnummer) {
      getBostadsInformationForId(selectedLagenhetsnummer.id);
    }
  }, [selectedLagenhetsnummer]);

  const clearForm = () => {
    setSelectedKommun('');
    setAdressOptions([]);
    setLagenhetsnummers([]);
    setSelectedLagenhetsnummer(undefined);
    setSelectedAdress(undefined);
    setAdressValue('');
    setBostad(undefined);
  };

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBostadsinformation({
      ...bostad,
      lagenhetsnummer: selectedLagenhetsnummer?.lagenhetsnummer,
    });
    setActiveVardering(undefined);
    clearForm();
  };

  const hasUnselectedLagenhetsnummers = () =>
    !!lagenhetsnummers?.length && !selectedLagenhetsnummer;

  return (
    <Fetcher
      fetchFn={varderingClient.getAutocompleteKommun}
      fetchFnArgs={[kommunValue]}
      defaultValue={[]}
      processData={(data) => ['', ...data]}
      render={({ data: kommuner, loadState: kommunerLoadState }) => (
        <form onSubmit={submitForm}>
          <Typography variant="h6">Välj adress</Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <KommunerAutocomplete
              kommuner={kommuner}
              loadState={kommunerLoadState}
              inputValue={kommunValue}
              setInputValue={setKommunValue}
              selectedValue={selectedKommun}
              setSelectedValue={setSelectedKommun}
            />
            <AdressAutocomplete
              adressOptions={adressOptions}
              loadState={adressLoadState}
              inputValue={adressValue}
              setInputValue={setAdressValue}
              setSelectedAdress={setSelectedAdress}
            />
            <LagenheterSelect
              lagenhetsnummers={lagenhetsnummers}
              loadState={lagenhetLoadState}
              selectedValue={selectedLagenhetsnummer?.lagenhetsnummer}
              setSelectedValue={setSelectedLagenhetsnummer}
            />
            <OptionalTooltip
              title={
                hasUnselectedLagenhetsnummers()
                  ? 'Välj en lägenhet för att gå vidare.'
                  : ''
              }
            >
              <Button
                size="small"
                variant="contained"
                type="submit"
                disabled={
                  !selectedAdress?.selectable ||
                  !bostad ||
                  lagenhetLoadState === LoadState.Loading
                }
              >
                Välj
              </Button>
            </OptionalTooltip>
          </Stack>
          {error && (
            <Alert sx={{ mt: 2 }} severity={error.severity}>
              {error.text}
            </Alert>
          )}
        </form>
      )}
    ></Fetcher>
  );
};
